import { useMemo } from 'react';

import type {
  ActionItem,
  ActionItemType,
  FollowUpActionListItem,
} from 'src/types/myFollowUp';

export interface ActionItemPagination {
  previousActionItem?: {
    id: number;
    offset?: number;
    type: ActionItemType;
    surveyId?: number;
  };
  nextActionItem?: {
    id: number;
    offset?: number;
    type: ActionItemType;
    surveyId?: number;
  };
}

export interface ActionItemPage {
  count: number;
  results: (ActionItem | FollowUpActionListItem)[];
}

interface UseActionItemPaginationProps {
  currentActionItemId?: number | null;
  actionItems: ActionItemPage;
  offset: number;
  pageSize: number;
  ordering: string;
  defaultType?: ActionItemType;
  useQuery: (
    offset: number,
    ordering: string,
    pageSize: number,
    enabled: boolean,
  ) => { data?: ActionItemPage };
}

const useActionItemPagination = ({
  currentActionItemId,
  actionItems,
  offset,
  pageSize,
  ordering,
  useQuery,
  defaultType = 'follow-up',
}: UseActionItemPaginationProps): ActionItemPagination => {
  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.ceil(actionItems.count / pageSize);

  const selectedItemIndex = useMemo(
    () =>
      actionItems.results?.findIndex(
        (item) => item.id === currentActionItemId,
      ) ?? -1,
    [actionItems.results, currentActionItemId],
  );

  const isValidNavigation =
    actionItems.results?.length > 0 &&
    selectedItemIndex !== -1 &&
    currentActionItemId;

  const { needsPrevPage, needsNextPage, previousOffset, nextOffset } =
    useMemo(() => {
      if (!isValidNavigation) {
        return {
          needsPrevPage: false,
          needsNextPage: false,
          previousOffset: 0,
          nextOffset: 0,
        };
      }

      const isFirstItem = selectedItemIndex === 0;
      const isLastItem = selectedItemIndex === actionItems.results.length - 1;
      const prevOffset =
        currentPage > 1 ? offset - pageSize : (totalPages - 1) * pageSize;
      const nextOffset = currentPage < totalPages ? offset + pageSize : 0;

      return {
        // Fetch previous page only if it's the first item and the previous page is different.
        needsPrevPage: isFirstItem && prevOffset !== offset,
        // Fetch next page only if it's the last item and the next page is different.
        needsNextPage: isLastItem && nextOffset !== offset,
        previousOffset: prevOffset,
        nextOffset,
      };
    }, [
      isValidNavigation,
      selectedItemIndex,
      actionItems.results?.length,
      currentPage,
      offset,
      pageSize,
      totalPages,
    ]);

  // Fetch data for the previous page if needed.
  const { data: previousPageItems } = useQuery(
    previousOffset,
    ordering,
    pageSize,
    needsPrevPage, // Boolean passed as the 'enabled' argument
  );

  // Fetch data for the next page if needed.
  const { data: nextPageItems } = useQuery(
    nextOffset,
    ordering,
    pageSize,
    needsNextPage,
  );

  return useMemo(() => {
    if (!isValidNavigation) {
      return { previousActionItem: undefined, nextActionItem: undefined };
    }

    const createNavigationItem = (item: any, itemOffset: number) => ({
      id: item.id,
      type: item.type || defaultType,
      surveyId: item.surveyId || null,
      offset: itemOffset,
    });

    const currentResults = actionItems.results;

    const previousActionItem = (() => {
      // If not the first item, the previous is on the same page.
      if (selectedItemIndex > 0) {
        return createNavigationItem(
          currentResults[selectedItemIndex - 1],
          offset,
        );
      }

      // If on the first item of a single page, wrap to the last item.
      if (previousOffset === offset) {
        return createNavigationItem(
          currentResults[currentResults.length - 1],
          offset,
        );
      }

      // If previous page data is available, take the last item from it.
      if (previousPageItems?.results?.length) {
        return createNavigationItem(
          previousPageItems.results[previousPageItems.results.length - 1],
          previousOffset,
        );
      }

      return undefined;
    })();

    const nextActionItem = (() => {
      if (selectedItemIndex < currentResults.length - 1) {
        return createNavigationItem(
          currentResults[selectedItemIndex + 1],
          offset,
        );
      }

      if (nextOffset === offset) {
        return createNavigationItem(currentResults[0], offset);
      }

      if (nextPageItems?.results?.length) {
        return createNavigationItem(nextPageItems.results[0], nextOffset);
      }

      return undefined;
    })();

    return { previousActionItem, nextActionItem };
  }, [
    isValidNavigation,
    selectedItemIndex,
    actionItems.results,
    offset,
    previousOffset,
    nextOffset,
    previousPageItems,
    nextPageItems,
    defaultType,
  ]);
};

export default useActionItemPagination;
